import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    AlertTriangle, Search, Loader2, Filter, Eye, CheckCircle, 
    Clock, XCircle, Image, Video, Calendar, User, Truck
} from 'lucide-react';
import { toast } from 'sonner';

export default function DamageReportsPage() {
    const { isAdmin } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterVehicle, setFilterVehicle] = useState('all');
    const [vehicles, setVehicles] = useState([]);
    
    // View modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [updating, setUpdating] = useState(false);

    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/damage-reports');
            setReports(response.data);
        } catch (error) {
            toast.error('Failed to fetch damage reports');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchVehicles = useCallback(async () => {
        try {
            const response = await api.get('/vehicles');
            setVehicles(response.data);
        } catch (error) {
            console.error('Failed to fetch vehicles');
        }
    }, []);

    useEffect(() => {
        fetchReports();
        fetchVehicles();
    }, [fetchReports, fetchVehicles]);

    const handleUpdateStatus = async (reportId, newStatus) => {
        setUpdating(true);
        try {
            await api.put(`/damage-reports/${reportId}?status=${newStatus}`);
            toast.success(`Report marked as ${newStatus}`);
            fetchReports();
            if (selectedReport?.id === reportId) {
                setSelectedReport({ ...selectedReport, status: newStatus });
            }
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setUpdating(false);
        }
    };

    const openViewModal = (report) => {
        setSelectedReport(report);
        setViewModalOpen(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><Clock className="w-3 h-3" /> Pending</span>;
            case 'reviewed':
                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Eye className="w-3 h-3" /> Reviewed</span>;
            case 'resolved':
                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" /> Resolved</span>;
            default:
                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{status}</span>;
        }
    };

    const filteredReports = reports.filter(report => {
        const matchesSearch = 
            report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.vehicle_info?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
        const matchesVehicle = filterVehicle === 'all' || report.vehicle_id === filterVehicle;
        return matchesSearch && matchesStatus && matchesVehicle;
    });

    const pendingCount = reports.filter(r => r.status === 'pending').length;
    const reviewedCount = reports.filter(r => r.status === 'reviewed').length;
    const resolvedCount = reports.filter(r => r.status === 'resolved').length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="damage-reports-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 font-heading flex items-center gap-2">
                        <AlertTriangle className="w-6 h-6 text-amber-500" />
                        Damage Reports
                    </h1>
                    <p className="text-slate-500 mt-1">Review and manage vehicle damage reports</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-800">{pendingCount}</p>
                            <p className="text-xs text-slate-500">Pending</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Eye className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-800">{reviewedCount}</p>
                            <p className="text-xs text-slate-500">Reviewed</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-800">{resolvedCount}</p>
                            <p className="text-xs text-slate-500">Resolved</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search reports..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-white border-slate-200"
                        data-testid="search-damage-reports"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[160px] bg-white border-slate-200" data-testid="filter-status">
                        <Filter className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                    <SelectTrigger className="w-[200px] bg-white border-slate-200" data-testid="filter-vehicle">
                        <Truck className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Vehicle" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Vehicles</SelectItem>
                        {vehicles.map(vehicle => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                                {vehicle.license_plate}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Reports Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Vehicle</th>
                                <th>Driver</th>
                                <th>Description</th>
                                <th>Media</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReports.length > 0 ? (
                                filteredReports.map((report) => (
                                    <tr key={report.id} className="table-row-hover">
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-slate-400" />
                                                <span className="text-slate-700">{report.created_at?.split('T')[0]}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Truck className="w-4 h-4 text-[#1B7EB5]" />
                                                <span className="text-slate-800 font-medium">{report.vehicle_info}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-slate-400" />
                                                <span className="text-slate-700">{report.driver_name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <p className="text-slate-600 truncate max-w-[200px]" title={report.description}>
                                                {report.description}
                                            </p>
                                        </td>
                                        <td>
                                            {report.media_url ? (
                                                <div className="flex items-center gap-1">
                                                    {report.media_type === 'video' ? (
                                                        <Video className="w-4 h-4 text-purple-500" />
                                                    ) : (
                                                        <Image className="w-4 h-4 text-blue-500" />
                                                    )}
                                                    <span className="text-xs text-slate-500 capitalize">{report.media_type}</span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">No media</span>
                                            )}
                                        </td>
                                        <td>{getStatusBadge(report.status)}</td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openViewModal(report)}
                                                    className="border-slate-200 text-slate-600 hover:text-[#1B7EB5]"
                                                    data-testid={`view-report-${report.id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {report.status === 'pending' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleUpdateStatus(report.id, 'reviewed')}
                                                        className="border-blue-200 text-blue-600 hover:bg-blue-50"
                                                        data-testid={`review-report-${report.id}`}
                                                    >
                                                        Mark Reviewed
                                                    </Button>
                                                )}
                                                {report.status === 'reviewed' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleUpdateStatus(report.id, 'resolved')}
                                                        className="border-green-200 text-green-600 hover:bg-green-50"
                                                        data-testid={`resolve-report-${report.id}`}
                                                    >
                                                        Mark Resolved
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-12">
                                        <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <p className="text-slate-500">No damage reports found</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Report Modal */}
            <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
                <DialogContent className="bg-white border-slate-200 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Damage Report Details
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedReport && (
                        <div className="space-y-6 mt-4">
                            {/* Status and Date */}
                            <div className="flex items-center justify-between">
                                {getStatusBadge(selectedReport.status)}
                                <span className="text-sm text-slate-500">
                                    {selectedReport.created_at?.split('T')[0]}
                                </span>
                            </div>

                            {/* Vehicle and Driver */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Truck className="w-4 h-4 text-[#1B7EB5]" />
                                        <span className="text-xs text-slate-500 uppercase">Vehicle</span>
                                    </div>
                                    <p className="text-slate-800 font-medium">{selectedReport.vehicle_info}</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <User className="w-4 h-4 text-[#58A6B4]" />
                                        <span className="text-xs text-slate-500 uppercase">Reported By</span>
                                    </div>
                                    <p className="text-slate-800 font-medium">{selectedReport.driver_name}</p>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="bg-slate-50 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    <span className="text-xs text-slate-500 uppercase">Damage Description</span>
                                </div>
                                <p className="text-slate-700">{selectedReport.description}</p>
                            </div>

                            {/* Media */}
                            {selectedReport.media_url && (
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        {selectedReport.media_type === 'video' ? (
                                            <Video className="w-4 h-4 text-purple-500" />
                                        ) : (
                                            <Image className="w-4 h-4 text-blue-500" />
                                        )}
                                        <span className="text-xs text-slate-500 uppercase">
                                            Attached {selectedReport.media_type}
                                        </span>
                                    </div>
                                    {selectedReport.media_type === 'image' ? (
                                        <img 
                                            src={`${process.env.REACT_APP_BACKEND_URL}/api/damage-reports/${selectedReport.id}/media?token=${localStorage.getItem('fleet_token')}`}
                                            alt="Damage" 
                                            className="w-full h-64 object-cover rounded-lg border border-slate-200"
                                        />
                                    ) : (
                                        <video 
                                            src={`${process.env.REACT_APP_BACKEND_URL}/api/damage-reports/${selectedReport.id}/media?token=${localStorage.getItem('fleet_token')}`}
                                            controls 
                                            className="w-full h-64 object-cover rounded-lg border border-slate-200"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3 pt-4 border-t border-slate-200">
                                {selectedReport.status === 'pending' && (
                                    <Button
                                        onClick={() => handleUpdateStatus(selectedReport.id, 'reviewed')}
                                        disabled={updating}
                                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                                    >
                                        {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                                        Mark as Reviewed
                                    </Button>
                                )}
                                {selectedReport.status === 'reviewed' && (
                                    <Button
                                        onClick={() => handleUpdateStatus(selectedReport.id, 'resolved')}
                                        disabled={updating}
                                        className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                                    >
                                        {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                        Mark as Resolved
                                    </Button>
                                )}
                                {selectedReport.status === 'resolved' && (
                                    <div className="flex-1 text-center py-2 bg-green-50 rounded-lg">
                                        <span className="text-green-600 font-medium flex items-center justify-center gap-2">
                                            <CheckCircle className="w-5 h-5" />
                                            This report has been resolved
                                        </span>
                                    </div>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={() => setViewModalOpen(false)}
                                    className="border-slate-200 text-slate-600"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
